import cn from 'classnames';
import { forwardRef } from 'react';

import { useFieldId } from '../../../../utils';
import { extractDataAttributes } from '../../../../utils/extract-data-aria-attributes';
import { BaseField, FieldLabel } from '../../../internal';
import Slider from '../slider';

import type { ForwardedRef, ReactElement } from 'react';

import type { SliderProps } from '../slider/slider.types';
import type { SliderFieldProps } from './slider-field.types';

import './slider-field.scss';

/**
 * SliderField wraps a Slider with a label, helper text, and optional tooltip.
 * Use this component when the slider needs to appear within a form with consistent field layout.
 *
 * ### Usage
 *
 * ```tsx
 * import { SliderField } from '@bloomreach/react-banana-ui';
 * import { useState } from 'react';
 *
 * export default function VolumeControl() {
 *   const [volume, setVolume] = useState(50);
 *
 *   return (
 *     <SliderField
 *       label="Volume"
 *       withNumberInput
 *       value={volume}
 *       onChange={(_, value) => setVolume(value as number)}
 *     />
 *   );
 * }
 * ```
 */
const SliderField = forwardRef((props: SliderFieldProps, forwardedRef: ForwardedRef<HTMLSpanElement>): ReactElement => {
  const { className, fullWidth, helperText, label, required, tooltip, ...rest } = props;

  const labelId = useFieldId(undefined, 'rbui-slider-field');

  // Split remaining props: data-* attributes go to the BaseField wrapper,
  // everything else is forwarded to the inner Slider component.
  const sliderPassThroughProps = Object.fromEntries(
    Object.entries(rest).filter(([key]) => !key.startsWith('data-')),
  ) as SliderProps;

  const dataAttributes = extractDataAttributes(rest);
  const isVertical = sliderPassThroughProps.orientation === 'vertical';
  const hasHorizontalMarkLabels =
    Boolean(helperText) &&
    !isVertical &&
    Array.isArray(sliderPassThroughProps.marks) &&
    sliderPassThroughProps.marks.some((mark) => mark.label != null);

  const fieldLabel = label && (
    <FieldLabel id={labelId} required={required} tooltip={tooltip}>
      {label}
    </FieldLabel>
  );

  const control = (
    <Slider
      {...sliderPassThroughProps}
      aria-labelledby={label ? labelId : sliderPassThroughProps['aria-labelledby']}
      ref={forwardedRef}
    />
  );

  return (
    <BaseField
      className={cn(className, 'rbui-slider-field', {
        'rbui-slider-field--vertical': isVertical,
        'rbui-slider-field--with-mark-labels': hasHorizontalMarkLabels,
      })}
      control={control}
      error={rest.error}
      fullWidth={fullWidth}
      helperText={helperText}
      label={fieldLabel}
      labelPosition="block-start"
      {...dataAttributes}
    />
  );
});

SliderField.displayName = 'SliderField';

export default SliderField;
